/**
 * ListingFeeStep — the listing wizard's "Listing Fee" step (billing-on only).
 *
 * Shows the Full Service fee for this listing and takes payment through the
 * existing Stripe one-time-charge flow (`OneTimeChargeModal`, which calls
 * `billingAgent.createOneTimeCharge`). Stripe's success comes back before the
 * webhook stamps the charge, so after success we poll
 * `billingAgent.getListingFee(houseId)` (≤10s) until `amount_due_cents === 0`.
 *
 * The parent gates Continue on `fee.amount_due_cents === 0`, so every fee
 * response is reported through `onFeeChange`.
 *
 * @param {object} props
 * @param {number|string|null} props.houseId - null while the draft is saving.
 * @param {string} [props.listingAddress]
 * @param {number|string} [props.listPrice]
 * @param {object|null} props.fee - last fee response (owned by the parent).
 * @param {(fee: object|null) => void} props.onFeeChange
 */
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { billingAgent } from '../../agents/billingAgent';
import { formatDollars } from '../../utils/pricing';
import styles from './ListingFeeStep.module.css';

// Lazy so Stripe.js only loads when the seller actually opens the payment form.
const OneTimeChargeModal = lazy(() => import('../Billing/OneTimeChargeModal'));

export const FEE_POLL_INTERVAL_MS = 1000;
export const FEE_POLL_TIMEOUT_MS = 10000;

const cents = (n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

export default function ListingFeeStep({ houseId, listingAddress, listPrice, fee, onFeeChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chargeOpen, setChargeOpen] = useState(false);
  // 'idle' | 'confirming' | 'timeout'
  const [confirmState, setConfirmState] = useState('idle');
  const [announcement, setAnnouncement] = useState('');
  const mountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  // Latest callback without making it a dependency: an inline parent callback
  // would otherwise re-run the load effect on every render.
  const onFeeChangeRef = useRef(onFeeChange);
  onFeeChangeRef.current = onFeeChange;
  const reportFee = useCallback((data) => {
    if (onFeeChangeRef.current) onFeeChangeRef.current(data);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const loadFee = useCallback(async () => {
    if (!houseId) return null;
    setLoading(true);
    setError(null);
    try {
      const data = await billingAgent.getListingFee(houseId);
      if (!mountedRef.current) return null;
      reportFee(data);
      return data;
    } catch (err) {
      if (mountedRef.current) {
        console.error('Listing fee lookup failed:', err?.status);
        setError(err?.message || "We couldn't load the fee for this listing.");
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [houseId, reportFee]);

  useEffect(() => {
    loadFee();
  }, [loadFee]);

  /** Poll until the webhook records the payment (amount due hits 0) or 10s pass. */
  const pollUntilPaid = useCallback(() => {
    const deadline = Date.now() + FEE_POLL_TIMEOUT_MS;
    setConfirmState('confirming');
    setAnnouncement('Payment received. Confirming…');
    const tick = async () => {
      let data = null;
      try {
        data = await billingAgent.getListingFee(houseId);
      } catch (_) {
        /* transient — keep polling until the deadline */
      }
      if (!mountedRef.current) return;
      if (data) {
        reportFee(data);
        if (cents(data.amount_due_cents) === 0) {
          setConfirmState('idle');
          setAnnouncement('Payment confirmed. You can continue.');
          return;
        }
      }
      if (Date.now() >= deadline) {
        setConfirmState('timeout');
        setAnnouncement('We are still confirming your payment.');
        return;
      }
      pollTimerRef.current = setTimeout(tick, FEE_POLL_INTERVAL_MS);
    };
    tick();
  }, [houseId, reportFee]);

  const handleChargeSucceeded = useCallback(() => {
    setChargeOpen(false);
    pollUntilPaid();
  }, [pollUntilPaid]);

  if (!houseId) {
    return (
      <div className={styles.step} data-testid="listing-fee-step">
        <h3 className={styles.title}>Listing fee</h3>
        <p className={styles.muted} role="status">Saving your listing draft…</p>
      </div>
    );
  }

  const tier = cents(fee?.tier_amount_cents);
  const paid = cents(fee?.paid_cents);
  const due = cents(fee?.amount_due_cents);
  const isPaid = Boolean(fee) && due === 0;

  return (
    <div className={styles.step} data-testid="listing-fee-step">
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      <h3 className={styles.title}>Listing fee</h3>

      {!fee && loading && (
        <p className={styles.muted} role="status" data-testid="listing-fee-loading">
          <span className={styles.spinner} aria-hidden="true" /> Loading your fee…
        </p>
      )}

      {!fee && error && (
        <div className={styles.errorBox} role="alert" data-testid="listing-fee-error">
          <p>{error}</p>
          <button type="button" className={styles.secondaryBtn} onClick={loadFee}>
            Try again
          </button>
        </div>
      )}

      {fee && (
        <div className={`${styles.card} ${isPaid ? styles.cardPaid : ''}`} data-testid="listing-fee-card">
          <div className={styles.row}>
            <span className={styles.rowLabel}>Full Service software fee</span>
            <span className={styles.rowValue} data-testid="listing-fee-tier">{formatDollars(tier)}</span>
          </div>
          {paid > 0 && (
            <div className={styles.row}>
              <span className={styles.rowLabel}>Already paid</span>
              <span className={styles.rowValue}>−{formatDollars(paid)}</span>
            </div>
          )}
          <div className={`${styles.row} ${styles.rowTotal}`}>
            <span className={styles.rowLabel}>{isPaid ? 'Status' : 'Due now'}</span>
            <span className={styles.rowValue} data-testid="listing-fee-due">
              {isPaid ? <span className={styles.paidPill}>Paid</span> : formatDollars(due)}
            </span>
          </div>

          <p className={styles.explainer}>
            A flat software fee for using the Cut the 6 platform to list and sell
            your home — not a commission and not a percentage of your sale. If we
            decline to publish your listing because we couldn&apos;t confirm your
            ownership of the property or your authority to sell it, we&apos;ll refund
            this fee in full to your original payment method.
          </p>

          {!isPaid && confirmState === 'idle' && (
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => setChargeOpen(true)}
              data-testid="listing-fee-pay-btn"
            >
              Pay {formatDollars(due)}
            </button>
          )}
          {confirmState === 'confirming' && (
            <p className={styles.muted} role="status" data-testid="listing-fee-confirming">
              <span className={styles.spinner} aria-hidden="true" /> Payment received — confirming…
            </p>
          )}
          {confirmState === 'timeout' && !isPaid && (
            <div className={styles.warnBox} role="status" data-testid="listing-fee-timeout">
              <p>
                Your payment went through, but we haven&apos;t finished recording it
                yet. This usually takes a few seconds.
              </p>
              <button type="button" className={styles.secondaryBtn} onClick={pollUntilPaid}>
                Check again
              </button>
            </div>
          )}
          {error && fee && <p className={styles.inlineError} role="alert">{error}</p>}
        </div>
      )}

      {chargeOpen && (
        <Suspense fallback={null}>
          <OneTimeChargeModal
            open={chargeOpen}
            purpose="listing_fee"
            houseId={houseId}
            listingAddress={listingAddress}
            listPrice={listPrice}
            onClose={() => setChargeOpen(false)}
            onSucceeded={handleChargeSucceeded}
          />
        </Suspense>
      )}
    </div>
  );
}
